import { deleteApi, postApi } from "../services/api.ts";
import { setContent, setError, setLoading } from "../services/dom.ts";
import { getStringFromLocalStorage } from "../services/localStorage.ts";
import { PlanTemplateDb } from "../services/indexeddb/planTemplateDb.ts";
import { RecipeDb } from "../services/indexeddb/recipeDb.ts";
import mealPlanItemTemplate from "../templates/components/mealplantemplateitem.html?raw";
import { EWeekday } from "../types/date.ts";
import { EMeal, IMealPlanTemplate } from "../types/plan.ts";
import { IRecipe } from "../types/recipe.ts";
import { IUser } from "../types/user.ts";
import Message from "./message.ts";

const apiUrl = import.meta.env.VITE_API_URL as string;
const apiToken = getStringFromLocalStorage("idToken");

const peopleOptions = [1, 2, 3, 4];

export default class MealPlanTemplateItem extends HTMLElement {
  private planTemplateDb = new PlanTemplateDb();
  private planTemplates!: IMealPlanTemplate[];
  private recipeDb = new RecipeDb();
  private recipeSelect: HTMLSelectElement;
  private recipePeopleSelect: HTMLSelectElement;
  private applyAllButton: HTMLButtonElement;
  private toggleLeftoversButton: HTMLButtonElement;
  private planTemplate!: IMealPlanTemplate;
  private planTemplateId: number;
  public message: Message;
  constructor(planTemplateId: number, user: IUser, day: EWeekday, meal: string) {
    super();

    // Populate the Element
    this.innerHTML = mealPlanItemTemplate;
    this.planTemplateId = planTemplateId;

    // Instantiate the Message
    this.message = new Message();

    // Set the Meal Heading
    this.querySelector("[data-selector=meal]")!.innerHTML = meal;

    // Get the Elements
    this.recipeSelect = this.querySelector("[data-selector=recipeSelect]") as HTMLSelectElement;
    this.recipePeopleSelect = this.querySelector("[data-selector=recipePeople]") as HTMLSelectElement;
    this.applyAllButton = this.querySelector("[data-selector=applyAll]") as HTMLButtonElement;
    this.toggleLeftoversButton = this.querySelector("[data-selector=toggleLeftovers]") as HTMLButtonElement;

    this.loadItem(user, day, meal);
  }

  private async loadItem(user: IUser, day: string, meal: string): Promise<void> {
    // Get the MealPlanTemplateItem if it exists, create an empty one otherwise. And populate relevant fields.
    this.planTemplates = await this.planTemplateDb.getPlanTemplates();
    this.planTemplate = this.planTemplates.find((searchPlanTemplate) => searchPlanTemplate.user.email === user.email && searchPlanTemplate.weekday === day && searchPlanTemplate.meal === meal) as IMealPlanTemplate;
    if (this.planTemplate) {
      this.recipeSelect.options.add(new Option(this.planTemplate.recipe.name, this.planTemplate.recipe.id.toString()));
      this.recipePeopleSelect.options.add(new Option(`x${this.planTemplate.number_people.toString()}`, this.planTemplate.number_people.toString()));
      if (this.planTemplate.is_leftovers) {
        this.setActive(this.toggleLeftoversButton.parentElement!);
      }
    } else {
      this.planTemplate = {
        template_id: this.planTemplateId,
        household: user.household,
        user: user,
        weekday: day as EWeekday,
        meal: meal as EMeal,
        recipe: {
          name: "--",
          id: 0,
          default_people: 1,
        },
        number_people: 1,
        is_leftovers: false,
      };
      this.recipePeopleSelect.options.add(new Option("x1", "1"));
    }

    // Populate remaining options feilds
    this.recipeSelect.options.add(new Option("--", "--"));
    const recipes = await this.recipeDb.getRecipes();
    recipes.forEach((recipe: IRecipe) => {
      this.recipeSelect.options.add(new Option(recipe.name, recipe.id.toString()));
    });
    peopleOptions.forEach((peopleOption) => {
      if (peopleOption != this.planTemplate.number_people) {
        this.recipePeopleSelect.options.add(new Option(`x${peopleOption.toString()}`, peopleOption.toString()));
      }
    });

    this.formatItem();

    // Add Event Listeners
    this.recipeSelect.addEventListener("change", (event: Event) => {
      this.selectRecipeForMeal(event);
    });
    this.recipePeopleSelect.addEventListener("change", (event: Event) => {
      this.selectMealMultiplier(event);
    });
    this.applyAllButton.addEventListener("click", () => {
      this.applyAll();
    });
    this.toggleLeftoversButton.addEventListener("click", () => {
      this.toggleLeftovers();
    });
  }

  private formatItem(): void {
    if (this.planTemplate.recipe.name && this.planTemplate.recipe.name != "--") {
      this.setActive(this.recipeSelect);
    } else {
      this.setInactive(this.recipeSelect);
    }

    if (this.planTemplate.is_leftovers) {
      this.setActive(this.toggleLeftoversButton.parentElement!);
    } else {
      this.setInactive(this.toggleLeftoversButton.parentElement!);
    }
  }

  private async selectRecipeForMeal(event: Event): Promise<void> {
    setLoading(this.recipeSelect);
    try {
      const target = event.target as HTMLSelectElement;
      const recipeName = target.selectedOptions[0].textContent as string;
      const newMealPlan = { ...this.planTemplate, recipe: { name: recipeName, id: 0, default_people: this.planTemplate.recipe.default_people } };
      await this.upsertMealPlan(newMealPlan);
      this.replaceMealPlan(newMealPlan);
      setContent(this.recipeSelect);
      this.formatItem();
    } catch (e: any) {
      console.log(e);
      this.message.timedMessage(`⚠ ${e} ⚠`);
      setError(this.recipeSelect);
      console.log(this.planTemplate.recipe.name);
      this.recipeSelect.value = this.planTemplate.recipe.id.toString();
      throw new Error(e);
    }
  }

  private async selectMealMultiplier(event: Event): Promise<void> {
    setLoading(this.recipePeopleSelect);
    try {
      const target = event.target as HTMLSelectElement;
      const reciplePeople = Number(target.selectedOptions[0].value);
      const newMealPlan = { ...this.planTemplate, number_people: reciplePeople };
      await this.upsertMealPlan(newMealPlan);
      this.replaceMealPlan(newMealPlan);
      setContent(this.recipePeopleSelect);
    } catch (e: any) {
      console.log(e);
      this.message.timedMessage(`⚠ ${e} ⚠`);
      setError(this.recipePeopleSelect);
      this.recipePeopleSelect.value = this.planTemplate.number_people.toString();
      throw new Error(e);
    }
  }

  private async toggleLeftovers(): Promise<void> {
    try {
      const newMealPlan = { ...this.planTemplate, is_leftovers: !this.planTemplate.is_leftovers };
      await this.upsertMealPlan(newMealPlan);
      this.planTemplate = newMealPlan;
      this.formatItem();
    } catch (e: any) {
      console.log(e);
      this.message.timedMessage(`⚠ ${e} ⚠`);
      setError(this.toggleLeftoversButton);
      this.toggleLeftoversButton.value = this.planTemplate.is_leftovers.toString();
    }
  }

  private async applyAll(): Promise<void> {}

  private async upsertMealPlan(mealPlan: IMealPlanTemplate): Promise<void> {
    const data: { [key: string]: string | number } = {};
    data.household_id = mealPlan.household.id;
    data.user_email = mealPlan.user.email;
    data.template_id = this.planTemplateId;
    data.weekday = mealPlan.weekday;
    data.meal = mealPlan.meal;

    try {
      if (mealPlan.recipe.name != "--") {
        data.recipe_name = mealPlan.recipe.name;
        data.number_people = mealPlan.number_people;

        data.is_leftovers = mealPlan.is_leftovers.toString();
        await postApi(apiUrl, "mealplantemplate", data, apiToken);
        await this.planTemplateDb.updatePlanTemplate(mealPlan);
      } else {
        await deleteApi(apiUrl, "mealplantemplate", undefined, apiToken, data);
      }
    } catch {
      throw new Error("Error setting MealPlan");
    }
  }

  private setActive(element: HTMLElement): void {
    element.classList.add("bg-gray-500");
    element.classList.remove("bg-gray-700");
  }

  private setInactive(element: HTMLElement): void {
    element.classList.add("bg-gray-700");
    element.classList.remove("bg-gray-500");
  }

  private replaceMealPlan(plan: IMealPlanTemplate): void {
    this.planTemplate = plan;
    // this.planTemplateDb.updatePlan(plan);
  }
}

customElements.define("meal-plan-template-item", MealPlanTemplateItem);
